import { FRAMEWORK_INDUSTRY_TYPES } from '../../Constants/frameworkGatedMenu';

/**
 * Field options and rules for the Knowledge Library page. The values match the IAM model
 * (iam_service pkg/knowledge_library): IAM validates every write against the same lists.
 */

const toOptions = (labels) => Object.entries(labels).map(([value, label]) => ({ value, label }));

export const DOC_TYPE_LABELS = {
  law: 'Law',
  guidance: 'Guidance',
  consultation: 'Consultation',
  expert_note: 'Expert note',
  exemplar: 'Exemplar',
  template: 'Template',
};
/** Authority tiers IAM accepts, in order. */
export const AUTHORITY_TIERS = ['L1', 'L2', 'L3', 'Z1', 'Z2', 'Z3'];
/** What each tier means. C (control catalogue) is not a library tier in IAM; it is named here so a C value still reads well. */
export const TIER_LABELS = {
  L1: 'Written law',
  L2: 'Regulator guidance',
  L3: 'Consultation',
  C: 'Control catalogue',
  Z1: 'Zavior note',
  Z2: 'Exemplar',
  Z3: 'Template',
};
/** "L1 Written law"; an unknown tier shows as itself. */
export const tierLabel = (tier) => (TIER_LABELS[tier] ? `${tier} ${TIER_LABELS[tier]}` : tier || '');
export const JURISDICTION_LABELS = {
  SG: 'Singapore',
  AU: 'Australia',
  INTL: 'International',
  US: 'United States',
  EU: 'European Union',
};
export const LEGAL_STATUS_LABELS = {
  in_force: 'In force',
  superseded: 'Superseded',
  consultation: 'Consultation',
};
export const LICENCE_MODE_LABELS = {
  quote_ok: 'Quote allowed',
  cite_only: 'Cite only',
};
export const STATUSES = ['Draft', 'In Review', 'Published', 'Retired'];
export const SOURCE_GIT = 'git';
export const SOURCE_UPLOAD = 'upload';

export const DOC_TYPE_OPTIONS = toOptions(DOC_TYPE_LABELS);
export const TIER_OPTIONS = AUTHORITY_TIERS.map((t) => ({ value: t, label: tierLabel(t) }));
export const SOURCE_OPTIONS = [{ value: SOURCE_GIT, label: 'From git' }, { value: SOURCE_UPLOAD, label: 'Upload' }];
export const JURISDICTION_OPTIONS = Object.entries(JURISDICTION_LABELS).map(([value, label]) => ({ value, label: `${label} (${value})` }));
export const LEGAL_STATUS_OPTIONS = toOptions(LEGAL_STATUS_LABELS);
/**
 * Legal statuses for law versions and amendments (ZVR-IAM-0048). Display only: the edit form
 * keeps offering the three values IAM accepts today.
 */
export const LAW_STATUS_LABELS = {
  ...LEGAL_STATUS_LABELS,
  pending_commencement: 'Pending commencement',
  repealed: 'Repealed',
};
export const legalStatusLabel = (value) => LAW_STATUS_LABELS[value] || value || '';
export const LICENCE_MODE_OPTIONS = toOptions(LICENCE_MODE_LABELS);
export const STATUS_OPTIONS = STATUSES.map((s) => ({ value: s, label: s }));

/** Industry choices: the framework industry types, plus any value already on an item. */
export const industryOptions = (extra = []) => [...new Set([...FRAMEWORK_INDUSTRY_TYPES, ...extra.filter(Boolean)])]
  .map((v) => ({ value: v, label: v }));

export const LIBRARY_KEY_PATTERN = /^[a-z0-9][a-z0-9._-]*$/;
export const LIBRARY_KEY_MAX = 120;

export const isGitRow = (row) => row?.source === SOURCE_GIT;

/** The reviewer's name from IAM (reviewed_by_name), else a short form of the user id. */
export function reviewerName(row) {
  if (!row?.reviewed_by) return '';
  return row.reviewed_by_name || `User ${String(row.reviewed_by).slice(0, 8)}`;
}

/** A date or timestamp as YYYY-MM-DD, or '' when missing. */
export const shortDate = (value) => (value ? String(value).slice(0, 10) : '');

/** YYYY-MM-DD as a local Date (new Date('YYYY-MM-DD') is UTC midnight and can show the day before). */
export function localDate(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd || ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

/** A Date as YYYY-MM-DD from its local parts. */
export function toYmd(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// --------------------------------------------------------------------------- files

/**
 * Files the AI library sync can parse (ingestion parser _MIME_SUFFIX), less the legacy and
 * spreadsheet or image types that do not suit a library document. Legacy .doc, .xls and .ppt
 * are refused with a save-as hint, as rejectLegacyOffice does elsewhere.
 */
export const LIBRARY_FILE_TYPES = {
  '.pdf': ['application/pdf'],
  '.docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  '.txt': ['text/plain'],
  '.md': ['text/markdown', 'text/x-markdown', 'text/plain'],
  '.html': ['text/html', 'application/xhtml+xml'],
  '.htm': ['text/html', 'application/xhtml+xml'],
};
export const LIBRARY_FILE_MAX_MB = 40; // the sync's MAX_FILE_BYTES
export const LIBRARY_FILE_HELP = `PDF, DOCX, TXT, Markdown (.md) or HTML, up to ${LIBRARY_FILE_MAX_MB} MB.`;
export const LIBRARY_FILE_REJECT_LEGACY = true;
export const LIBRARY_FILE_ACCEPT = Object.keys(LIBRARY_FILE_TYPES).join(',');
const LEGACY_OFFICE = /\.(doc|xls|ppt)$/i;
const LEGACY_MIME = ['application/msword', 'application/vnd.ms-excel', 'application/vnd.ms-powerpoint'];

/** Why a file cannot be uploaded to the library, or '' when it can. */
export function libraryFileProblem(file) {
  const name = String(file?.name || '').toLowerCase();
  if (LIBRARY_FILE_REJECT_LEGACY && (LEGACY_OFFICE.test(name) || LEGACY_MIME.includes(file?.type))) {
    return 'Legacy .doc, .xls and .ppt files cannot be read. Save it as .docx or PDF, then upload it again.';
  }
  const ext = Object.keys(LIBRARY_FILE_TYPES).find((e) => name.endsWith(e));
  if (!ext || (file?.type && !LIBRARY_FILE_TYPES[ext].includes(file.type))) {
    return `This file type cannot be read by the library sync. Use ${LIBRARY_FILE_HELP}`;
  }
  if (file.size > LIBRARY_FILE_MAX_MB * 1024 * 1024) return `The file is larger than ${LIBRARY_FILE_MAX_MB} MB.`;
  if (name.length > 150) return 'The file name is longer than 150 characters.';
  return '';
}

// --------------------------------------------------------------------------- form

/** Comma or line separated text as a trimmed list without blanks or repeats. */
export const splitList = (text) => [...new Set(String(text || '').split(/[,\n]/).map((s) => s.trim()).filter(Boolean))];

/** The form state for an item (or a blank upload). Lists other than industries are edited as comma text. */
export const formFromItem = (item = {}) => ({
  library_key: item.library_key || '',
  title: item.title || '',
  doc_type: item.doc_type || '',
  authority_tier: item.authority_tier || '',
  jurisdiction: item.jurisdiction || '',
  issuer: item.issuer || '',
  industries: Array.isArray(item.industries) ? item.industries : [],
  frameworks: (item.frameworks || []).join(', '),
  sectors: (item.sectors || []).join(', '),
  as_of: item.as_of || '',
  legal_status: item.legal_status || '',
  licence_mode: item.licence_mode || '',
  source_url: item.source_url || '',
  file_id: item.file_id || '',
  status: item.status || 'Draft',
  review_due: item.review_due || '',
});

const REQUIRED_LABELS = {
  library_key: 'Library key',
  title: 'Title',
  doc_type: 'Document type',
  authority_tier: 'Authority tier',
  jurisdiction: 'Jurisdiction',
  licence_mode: 'Licence mode',
  status: 'Status',
};
const REQUIRED = Object.keys(REQUIRED_LABELS);
const OPTIONAL_TEXT = ['issuer', 'as_of', 'source_url', 'file_id', 'review_due'];

/** Problems the form can catch before IAM does; IAM checks the rest and answers 400 with its own list. */
export function validateForm(form, { isCreate }) {
  const problems = [];
  REQUIRED.forEach((field) => {
    if (!String(form[field] || '').trim()) problems.push(`${REQUIRED_LABELS[field]} is required`);
  });
  if (isCreate && form.library_key) {
    if (form.library_key.length > LIBRARY_KEY_MAX) problems.push(`Library key is longer than ${LIBRARY_KEY_MAX} characters`);
    else if (!LIBRARY_KEY_PATTERN.test(form.library_key)) {
      problems.push('Library key must be lower-case letters, digits, dots, dashes or underscores, starting with a letter or digit');
    }
  }
  return problems;
}

/** The body of POST /knowledge-library: an upload, without review fields (IAM records the reviewer). */
export function buildCreateBody(form) {
  const body = { source: SOURCE_UPLOAD };
  REQUIRED.forEach((field) => { body[field] = String(form[field]).trim(); });
  OPTIONAL_TEXT.forEach((field) => {
    const value = String(form[field] || '').trim();
    if (value) body[field] = value;
  });
  if (form.legal_status) body.legal_status = form.legal_status;
  body.industries = form.industries || [];
  body.frameworks = splitList(form.frameworks);
  body.sectors = splitList(form.sectors);
  return body;
}

const sameList = (a, b) => JSON.stringify(a || []) === JSON.stringify(b || []);

/**
 * The body of PUT /knowledge-library/:id: only the fields that changed. A cleared optional text
 * field is sent as null (IAM clears it). A required field or legal_status is never sent empty,
 * because IAM reads null or "" there as "not provided" and keeps the stored value.
 */
export function buildPatch(item, form) {
  const before = formFromItem(item);
  const patch = {};
  REQUIRED.filter((f) => f !== 'library_key').forEach((field) => {
    const value = String(form[field] || '').trim();
    if (value && value !== before[field]) patch[field] = value;
  });
  OPTIONAL_TEXT.forEach((field) => {
    const value = String(form[field] || '').trim();
    if (value !== before[field]) patch[field] = value || null;
  });
  if (form.legal_status && form.legal_status !== before.legal_status) patch.legal_status = form.legal_status;
  if (!sameList(form.industries, item.industries)) patch.industries = form.industries || [];
  const frameworks = splitList(form.frameworks);
  if (!sameList(frameworks, item.frameworks)) patch.frameworks = frameworks;
  const sectors = splitList(form.sectors);
  if (!sameList(sectors, item.sectors)) patch.sectors = sectors;
  return patch;
}

// --------------------------------------------------------------------------- list filters

// Fields whose values come from a fixed list: a filter on them matches exactly.
const ENUM_FILTER_FIELDS = new Set(['doc_type', 'authority_tier', 'jurisdiction', 'status', 'source', 'sync_state']);

/**
 * Whether a row passes one filter (a quick filter chip or a column filter). Enum fields match
 * exactly, industries (a list) when it holds the value, and text fields as a case-insensitive
 * substring. A multi-select filter (an array) passes when any of its values does.
 */
export function matchesFilter(row, field, value) {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0 || value.some((v) => matchesFilter(row, field, v));
  if (field === 'industries') return (row.industries || []).includes(value);
  if (ENUM_FILTER_FIELDS.has(field)) return row[field] === value;
  return String(row[field] ?? '').toLowerCase().includes(String(value).toLowerCase());
}

/** Rows ordered by a { field, direction } sort config, compared as lower-case text like StandardListView does. */
export function sortRows(rows, sort) {
  if (!sort?.field) return rows;
  const text = (v) => (Array.isArray(v) ? v.join(', ') : String(v ?? '')).toLowerCase();
  const dir = sort.direction === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const x = text(a[sort.field]);
    const y = text(b[sort.field]);
    if (x < y) return -dir;
    if (x > y) return dir;
    return 0;
  });
}

// --------------------------------------------------------------------------- sync state

export const SYNC_LABELS = {
  synced: 'Synced',
  pending: 'Pending',
  error: 'Error',
  no_content: 'No content',
  off: 'Off',
};

/**
 * One store's state for a row. store is 'qdrant' or 'provider'. The source is the AI status
 * row's own state (synced, no_content or failed, from library_sync_state), never a comparison
 * of IAM and AI timestamps, which come from two clocks.
 *
 * - Only live Published rows sync; other rows have no state (null).
 * - With no provider configured the provider badge is 'off'.
 * - A row with no file_id has nothing to embed: 'no_content'.
 * - A Published row the AI does not hold yet is 'pending'.
 * - 'failed' is an error on the store that failed. A provider failure is recorded as
 *   "<provider>: reason" after Qdrant was written, so Qdrant still shows synced.
 */
export function storeSyncState(row, statusItem, store, provider) {
  if (!row || row.status !== 'Published' || row.is_deleted) return null;
  const providerOff = !provider || provider === 'none';
  if (store === 'provider' && providerOff) return 'off';
  if (!row.file_id) return 'no_content';
  if (!statusItem) return 'pending';
  switch (statusItem.state) {
    case 'no_content':
      return 'no_content';
    case 'failed': {
      const providerOnly = !providerOff && String(statusItem.error || '').startsWith(`${provider}:`);
      return store === 'qdrant' && providerOnly ? 'synced' : 'error';
    }
    case 'synced':
      return store === 'qdrant' || statusItem.provider_synced_at ? 'synced' : 'pending';
    default:
      return 'pending';
  }
}

/** The row's overall state for the quick filter: error, then pending, then no content, then synced. */
export function rowSyncState(row, statusItem, provider) {
  const states = ['qdrant', 'provider'].map((s) => storeSyncState(row, statusItem, s, provider)).filter((s) => s && s !== 'off');
  if (!states.length) return null;
  return ['error', 'pending', 'no_content', 'synced'].find((s) => states.includes(s));
}

/** "Synced 4, updated 1, removed 0, no content 0, failed 0" from a counts object. */
export function formatSyncCounts(counts) {
  if (!counts || typeof counts !== 'object') return '';
  const parts = [
    ['synced', 'Synced'],
    ['updated_metadata', 'updated'],
    ['removed', 'removed'],
    ['no_content', 'no content'],
    ['failed', 'failed'],
  ].filter(([key]) => counts[key] !== undefined && counts[key] !== null).map(([key, label]) => `${label} ${counts[key]}`);
  return parts.join(', ');
}

export const PROVIDER_LABELS = { none: 'No provider', azure_search: 'Azure AI Search', vertex_rag: 'Vertex RAG' };
